'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { Button, CircularProgress } from '@mui/material';
import { useWorkshopList, deleteWorkshop, fetchAllWorkshop } from '../../../stores/workshopStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import FormDialog from '../../../components/FormDialog';
import WorkshopAddEdit from './WorkshopAddEdit';

import { useLanguage } from '../../../utils/i18n';

function WorkshopPageContent() {
  const { t } = useLanguage();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const formRef = useRef();

  const notify = useNotifier();
  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (!stored) return;

      const match = companyOptionsNormalized.find((o) => String(o.value) === String(stored));
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: String(stored), label: String(stored) }];

      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company failed', e);
    }
  }, [companyOptionsNormalized]);

  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.all', 'All') },
      { value: 'name', label: t('workshop.workshopName', 'Workshop Name') },
      { value: 'cp_name', label: t('workshop.contactPerson', 'Contact Person') },
      { value: 'cp_number', label: t('workshop.contactNumber', 'Contact Number') },
      { value: 'address', label: t('workshop.address', 'Address') }
    ],
    [t]
  );

  const companyIdsString = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';

    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';

    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = String(searchRaw ?? '').trim();
      setSearchValue((prev) => (prev === next ? prev : next));
      setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(timer);
  }, [searchRaw]);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading
  } = useWorkshopList(paginationModel.page, paginationModel.pageSize, searchField, searchValue, companyIdsString);

  const handleFetchAllForExport = async () => {
    try {
      return await fetchAllWorkshop({
        searchKey: searchField,
        searchValue,
        selectedCompanyIds: companyIdsString,
        total: totalRecords
      });
    } catch (err) {
      console.error('workshop export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => setPaginationModel(model);
  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };
  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
    setLoadingForm(false);
  };
  const handleDialogSave = () => {
    formRef.current?.submitForm?.();
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('error storage');
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id) => {
    setConfirmTargetId(id);
    setConfirmOpen(true);
  };
  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteWorkshop(confirmTargetId);
      notify({ message: t('workshop.deletedSuccess', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.warn(err);
      notify({ message: t('workshop.failedDelete', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
    }
  };

  const handleDialogSuccess = (msg) => {
    notify({ message: msg || t('workshop.savedSuccess', 'Saved successfully'), severity: 'success' });
    handleDialogClose();
    setPaginationModel((p) => ({ ...p }));
  };

  const getCompanyLabel = (row) => {
    if (row?.join?.company_name) return row.join.company_name;
    const companyId = row?.company ?? row?.company_id;
    const found = companyOptionsNormalized.find((c) => String(c.value) === String(companyId));
    return found ? found.label : '-';
  };

  const columns = [
    { field: 'company', headerName: t('general.company', 'Company'), flex: 1, renderCell: (params) => getCompanyLabel(params.row) },
    { field: 'name', headerName: t('workshop.workshopName', 'Workshop Name'), flex: 1.5 },
    { field: 'cp_name', headerName: t('workshop.cpName', 'CP Name'), flex: 1 },
    { field: 'cp_number', headerName: t('workshop.cpPhoneNumber', 'CP Phone Number'), flex: 1 },
    { field: 'address', headerName: t('workshop.address', 'Address'), flex: 2 },
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      renderCell: (params) => <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={handleDelete} mode="inline" />
    }
  ];

  return (
    <div>
      <DataTable
        title={t('workshop.title', 'Workshop')}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value);
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
        }}
        onAddClick={handleAddClick}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        hideExportButton={false}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <FormDialog
        open={openDialog}
        onClose={handleDialogClose}
        title={editingId ? t('workshop.editWorkshop', 'Edit Workshop') : t('workshop.addWorkshop', 'Add Workshop')}
        maxWidth="md"
        actions={
          <>
            <Button onClick={handleDialogClose} sx={{ textTransform: 'none' }}>
              {t('buttonTitle.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={loadingForm}
              onClick={handleDialogSave}
              sx={{ color: 'primary', textTransform: 'none' }}
              startIcon={loadingForm ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {loadingForm ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
            </Button>
          </>
        }
      >
        <WorkshopAddEdit
          ref={formRef}
          id={editingId}
          onClose={handleDialogClose}
          onSuccess={handleDialogSuccess}
          onError={(msg) => notify({ message: msg, severity: 'error' })}
          onSubmitStart={() => setLoadingForm(true)}
          onSubmitEnd={() => setLoadingForm(false)}
        />
      </FormDialog>

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
      />
    </div>
  );
}

export default function WorkshopPage() {
  return (
    <>
      <WorkshopPageContent />
    </>
  );
}
